/**
 * useClip — React hook for CNF web-clip mode (Sub-spec J, Block J-C).
 *
 * Manages per-field "waiting for clip" state and handles the socket
 * clip:filled event from the backend.  When a clip:filled event arrives
 * that matches the active record + field, the hook exposes the filled value
 * so the caller can pre-fill the relevant form field and show a confirm step.
 *
 * Owner/advisor only: callers should gate the clip affordance via useHasRole
 * before mounting; this hook itself does NOT enforce the role so that it stays
 * composable.
 *
 * Feature flag: only active when REACT_APP_WEB_CLIP === 'true'.
 *
 * WP-06 Part 1 — the waiting state is now ALWAYS terminating:
 *  - `setClipTarget()` resolves extensionPaired=false after its ack timeout when
 *    no extension answered.  That is now a terminal failure state, not a silent
 *    "keep waiting" (previously only `isPending` was cleared, so the spinner ran
 *    forever — which is exactly what mobile users hit, since a phone has no
 *    extension runtime at all).
 *  - When pairing DOES succeed, a hard timeout matching the backend clip-session
 *    TTL flips the waiting state to a terminal failure, so the spinner always
 *    ends in success or a stated failure.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  setClipTarget,
  ClipTargetField,
  ClipFilledPayload,
  CLIP_FILL_TIMEOUT_MS,
} from '../services/clipApi';
import { socketService } from '../services/socketService';
import { logger } from '../utils/logger';
import {
  canPairWithExtension,
  isExtensionCapableBrowser,
  markExtensionUnreachable,
  resetExtensionUnreachable,
} from '../utils/extensionEnvironment';

/**
 * Read the flag at call time rather than module-load time so that the value is
 * observable in tests and consistent with ClipButton (which already reads it at
 * render time). CRA still inlines the literal at build time.
 */
function isWebClipEnabled(): boolean {
  return process.env.REACT_APP_WEB_CLIP === 'true';
}

/** Why a clip attempt ended without a value. */
export type ClipFailureReason = 'extension_unreachable' | 'timeout';

export interface ClipFieldState {
  /** The field currently waiting for a clip from the extension. */
  waitingField: ClipTargetField | null;
  /** Whether the backend pairing succeeded. */
  isPending: boolean;
  /** Whether the extension acknowledged the pair message. */
  extensionPaired: boolean;
  /** A value that was just filled via clip:filled — pending user confirmation. */
  filledField: ClipTargetField | null;
  filledValue: string | null;
  /** The field whose clip attempt ended in a terminal failure. */
  failedField: ClipTargetField | null;
  failureReason: ClipFailureReason | null;
}

export interface UseClipResult {
  /** True only when the feature flag is on. */
  enabled: boolean;
  /**
   * True when the companion extension could be present in this browser.
   * False on mobile (no extension runtime exists) and after a pair attempt has
   * proved the extension absent.  Callers MUST NOT offer a clip affordance when
   * this is false — the clip can never complete there.
   */
  extensionSupported: boolean;
  state: ClipFieldState;
  /**
   * Activate clip mode for a given field.
   * Calls POST /api/clip/target → sends the pair message → transitions to
   * "waiting" state.  Rejects with an error message string on failure.
   */
  activateClip: (
    recordType: 'estimate' | 'work_order',
    recordId: number,
    field: ClipTargetField,
  ) => Promise<void>;
  /** Clear the runtime "extension absent" memory and activate again. */
  retryClip: (
    recordType: 'estimate' | 'work_order',
    recordId: number,
    field: ClipTargetField,
  ) => Promise<void>;
  /** Clear the waiting / filled state (e.g. after user confirms or cancels). */
  clearClip: () => void;
  /** Dismiss only the filled-value overlay (keep waiting state). */
  clearFilled: () => void;
  /** Dismiss a terminal failure notice. */
  clearFailure: () => void;
}

const INITIAL_STATE: ClipFieldState = {
  waitingField: null,
  isPending: false,
  extensionPaired: false,
  filledField: null,
  filledValue: null,
  failedField: null,
  failureReason: null,
};

export function useClip(
  /** The record type this hook instance is bound to. */
  recordType: 'estimate' | 'work_order',
  /** The record ID this hook instance is bound to. */
  recordId: number,
): UseClipResult {
  const [state, setState] = useState<ClipFieldState>(INITIAL_STATE);
  // Held in state (not derived per render) so that marking the extension
  // unreachable re-renders every clip affordance on the page.
  const [extensionSupported, setExtensionSupported] = useState<boolean>(() =>
    canPairWithExtension(),
  );

  // Track the current active (recordType, recordId) pair so the socket handler
  // only fires for the right record — even after re-renders.
  const activeRef = useRef<{ recordType: string; recordId: number } | null>(null);
  // Hard timeout on the waiting state so it can never persist indefinitely.
  const fillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFillTimer = useCallback(() => {
    if (fillTimerRef.current !== null) {
      clearTimeout(fillTimerRef.current);
      fillTimerRef.current = null;
    }
  }, []);

  const armFillTimer = useCallback(
    (field: ClipTargetField) => {
      clearFillTimer();
      fillTimerRef.current = setTimeout(() => {
        fillTimerRef.current = null;
        logger.warn('[clip] waiting for clip timed out', { field });
        setState((prev) =>
          prev.waitingField === field
            ? {
                ...prev,
                waitingField: null,
                isPending: false,
                failedField: field,
                failureReason: 'timeout',
              }
            : prev,
        );
      }, CLIP_FILL_TIMEOUT_MS);
    },
    [clearFillTimer],
  );

  const activateClip = useCallback(
    async (
      rt: 'estimate' | 'work_order',
      rid: number,
      field: ClipTargetField,
    ) => {
      if (!isWebClipEnabled()) return;

      // Loud guard: a caller must never activate clip mode in a browser that
      // cannot host the extension — the ack can never arrive there.
      if (!isExtensionCapableBrowser()) {
        throw new Error(
          'Web clip needs the CNF browser extension, which this browser cannot run. Use manual entry instead.',
        );
      }

      setState({
        ...INITIAL_STATE,
        waitingField: field,
        isPending: true,
      });

      activeRef.current = { recordType: rt, recordId: rid };

      try {
        const { extensionPaired } = await setClipTarget(rt, rid, field);

        if (!extensionPaired) {
          // The extension never acknowledged — it is not installed or not active
          // in this browser. Terminal: do NOT leave the field waiting.
          markExtensionUnreachable();
          setExtensionSupported(false);
          clearFillTimer();
          activeRef.current = null;
          setState({
            ...INITIAL_STATE,
            failedField: field,
            failureReason: 'extension_unreachable',
          });
          logger.warn('[clip] extension did not acknowledge pairing', { field });
          return;
        }

        setState((prev) => ({ ...prev, isPending: false, extensionPaired: true }));
        armFillTimer(field);
      } catch (err: any) {
        clearFillTimer();
        setState(INITIAL_STATE);
        activeRef.current = null;
        const msg: string =
          err?.response?.data?.error ?? err?.message ?? 'Clip target request failed';
        throw new Error(msg);
      }
    },
    [armFillTimer, clearFillTimer],
  );

  const retryClip = useCallback(
    async (
      rt: 'estimate' | 'work_order',
      rid: number,
      field: ClipTargetField,
    ) => {
      resetExtensionUnreachable();
      setExtensionSupported(canPairWithExtension());
      await activateClip(rt, rid, field);
    },
    [activateClip],
  );

  const clearClip = useCallback(() => {
    clearFillTimer();
    setState(INITIAL_STATE);
    activeRef.current = null;
  }, [clearFillTimer]);

  const clearFilled = useCallback(() => {
    setState((prev) => ({ ...prev, filledField: null, filledValue: null }));
  }, []);

  const clearFailure = useCallback(() => {
    setState((prev) => ({ ...prev, failedField: null, failureReason: null }));
  }, []);

  // Socket listener — subscribe to clip:filled and filter by active record.
  useEffect(() => {
    if (!isWebClipEnabled()) return;

    const handler = (payload: ClipFilledPayload) => {
      const active = activeRef.current;
      if (
        !active ||
        payload.recordType !== active.recordType ||
        payload.recordId !== active.recordId
      ) {
        // Not for this record instance — ignore silently.
        return;
      }

      logger.log('[clip] clip:filled received', payload);
      clearFillTimer();
      setState((prev) => ({
        ...prev,
        waitingField: null,
        isPending: false,
        failedField: null,
        failureReason: null,
        filledField: payload.targetField,
        filledValue: payload.value,
      }));
    };

    socketService.on('clip:filled', handler);
    return () => {
      socketService.off('clip:filled', handler);
    };
  }, [clearFillTimer]);

  // Release the hard timeout when the hook unmounts.
  useEffect(() => clearFillTimer, [clearFillTimer]);

  // Auto-subscribe to the entity channel so clip:filled is received even if
  // the parent component has not already joined the room.  (Harmless duplicate
  // if the parent has already joined.)
  useEffect(() => {
    if (!isWebClipEnabled() || !recordId) return;
    if (recordType === 'estimate' || recordType === 'work_order') {
      socketService.joinEntityChannel(recordType, recordId);
    }
  }, [recordType, recordId]);

  return {
    enabled: isWebClipEnabled(),
    extensionSupported,
    state,
    activateClip,
    retryClip,
    clearClip,
    clearFilled,
    clearFailure,
  };
}
